<template>
    <ValidationObserver ref="form" v-slot="{ invalid }">
        <div class="absolute top-0 left-0 right-0 bottom-0 w-full overflow-hidden flex items-center text-center justify-center z-50" v-show="loading"><spinner line-fg-color="#922C49" size="55" :line-size="5"></spinner></div>
        <form id="form" v-if="!loading" @submit.prevent="submitForm" class="flex flex-col h-full" autocomplete="off">
            <page-header :buttons="pageActions.buttons" :additional="pageActions.additional" @method="methodHandler">
                <template slot="title">{{ type }} Supplier</template>
            </page-header>

            <tabs :tabs="tabs" :active="startTab" @setTab="setActiveTab" ref="tabs"></tabs>
            <div class="bg-red-200 py-2 text-sm text-red-700 text-center w-full -mt-px" role="alert" v-show="invalid && submitted" >Please complete all required fields</div>
            <div class="bg-red-200 py-2 text-sm text-red-700 text-center w-full -mt-px" v-if="errorMessage">{{ errorMessage }}</div>

            <div class="flex md:flex-row sm:flex-col flex-grow">
                <div class="md:w-3/4 sm:w-full px-4 sm:px-6 lg:px-6 bg-gray-200 border-r border-gray-400 pb-12" v-show="activeTab === 'details'">
                    <ValidationObserver vid="details" :slim="true">
                        <div class="mt-6 grid grid-cols-1 row-gap-5 col-gap-4 sm:grid-cols-12">
                            <input-text class="sm:col-span-6"
                                label="Supplier Name"
                                placeholder="Supplier Name"
                                name="name"
                                v-model="item.name"
                                rules="required"
                                :required="true" />
                            <input-text class="sm:col-span-6"
                                label="ABN"
                                placeholder="ABN"
                                name="abn"
                                v-model="item.abn"
                                :required="false"
                                :mask="'## ### ### ###'"/>
                            <input-text class="sm:col-span-12"
                                label="Sale URL"
                                placeholder="Sale URL"
                                name="website"
                                v-model="item.website"
                                :required="false" />
                        </div>
                        <div class="mt-6 grid grid-cols-1 row-gap-5 col-gap-4 sm:grid-cols-12">
                            <h2 class="w-full col-span-12 pb-2 border-b-2 border-charcoal-900 text-left font-medium text-charcoal-900">Contact</h2>
                            <input-text class="sm:col-span-6"
                                label="First Name"
                                placeholder="First Name"
                                name="contact_first"
                                v-model="item.contact_first" />
                            <input-text class="sm:col-span-6"
                                label="Last Name"
                                placeholder="Last Name"
                                name="contact_last"
                                v-model="item.contact_last" />
                            <input-text class="sm:col-span-4"
                                label="Primary Email"
                                placeholder="Email"
                                name="email"
                                v-model="item.email"
                                :required="false"/>
                            <input-text class="sm:col-span-4"
                                label="Mobile"
                                placeholder="Mobile"
                                name="mobile"
                                v-model="item.mobile"
                                :required="!item.phone"
                                :rules="!item.phone ? 'required' : ''"
                                :mask="'#### ### ###'"/>
                            <input-text class="sm:col-span-4"
                                label="Phone"
                                placeholder="Phone"
                                name="phone"
                                v-model="item.phone"
                                :required="!item.mobile"
                                :rules="!item.mobile ? 'required' : ''"/>
                            <input-text class="sm:col-span-12"
                                label="Additional Emails"
                                placeholder="Comma Separated Email Addresses"
                                name="additional_emails"
                                v-model="item.additional_emails"
                                :required="false"/>
                        </div>
                        <div class="mt-6 grid xl:grid-cols-2 lg:grid-cols-2 row-gap-5 col-gap-4 sm:grid-cols-12">
                            <div class="grid row-gap-2 col-gap-4">
                                <h2 class="w-full col-span-12 py-2 border-b-2 border-charcoal-900 text-left font-medium text-charcoal-900 mb-2">Street Address</h2>
                                <input-address-autocomplete class="col-span-12"
                                    label="Address"
                                    placeholder="Address"
                                    name="address"
                                    v-model="item.address"
                                    rules="required"
                                    :required="true"
                                    :separate="true"
                                    @selected="setAddress"/>
                                <input-text class="col-span-6"
                                    label="Suburb"
                                    placeholder="Suburb"
                                    name="suburb"
                                    v-model="item.suburb"
                                    rules="required"
                                    :required="true" />
                                <input-select class="sm:col-span-6"
                                    label="State"
                                    name="state"
                                    placeholder="State"
                                    :options="options.states"
                                    v-model="item.state"
                                    :initial-value="initialValue.state"
                                    rules="required"
                                    :required="true"
                                    return="label"/>
                                <input-text class="sm:col-span-6"
                                    label="Postcode"
                                    placeholder="Postcode"
                                    name="postcode"
                                    v-model="item.postcode"
                                    rules="required"
                                    :required="true" />
                                <input-text class="sm:col-span-6"
                                    label="Country"
                                    placeholder="Country"
                                    name="country"
                                    v-model="item.country"
                                    rules="required"
                                    :required="true" />
                            </div>
                            <div class="grid row-gap-2 col-gap-4">
                                <h2 class="w-full sm:col-span-12 py-2 border-b-2 border-charcoal-900 text-left font-medium text-charcoal-900 mb-2">Postal Address</h2>
                                <input-text class="sm:col-span-12"
                                    label="Address"
                                    placeholder="Address"
                                    name="postal_address"
                                    v-model="item.postal_address"
                                    :required="false" />
                                <input-text class="sm:col-span-6"
                                    label="Suburb"
                                    placeholder="Suburb"
                                    name="postal_suburb"
                                    v-model="item.postal_suburb"
                                    :required="false" />
                                <input-select class="sm:col-span-6"
                                    label="State"
                                    name="postal_state"
                                    placeholder="State"
                                    :options="options.states"
                                    v-model="item.postal_state"
                                    :initial-value="initialValue.postal_state"
                                    :required="false"
                                    return="label"/>
                                <input-text class="sm:col-span-6"
                                    label="Postcode"
                                    placeholder="Postcode"
                                    name="postal_postcode"
                                    v-model="item.postal_postcode"
                                    :required="false" />
                                <input-text class="sm:col-span-6"
                                    label="Country"
                                    placeholder="Country"
                                    name="postal_country"
                                    v-model="item.postal_country"
                                    :required="false" />
                            </div>
                        </div>
                        <div class="mt-2 grid grid-cols-1 row-gap-5 col-gap-4 sm:grid-cols-12">
                            <h2 class="w-full sm:col-span-12 py-2 border-b-2 border-charcoal-900 text-left font-medium text-charcoal-900">Bank Account</h2>
                            <input-text class="sm:col-span-4"
                                label="Account Name"
                                placeholder="Account Name"
                                name="account_name"
                                v-model="item.account_name"
                                :required="false"
                                :max-length="32"
                            />
                            <input-text class="sm:col-span-4"
                                label="BSB"
                                name="account_bsb"
                                placeholder="BSB"
                                v-model="item.account_bsb"
                                :required="false" />
                            <input-text class="sm:col-span-4"
                                label="Account Number"
                                name="account_number"
                                placeholder="Account Number"
                                v-model="item.account_number"
                                :required="false" />
                        </div>
                        <div class="mt-2 grid grid-cols-1 row-gap-5 col-gap-4 sm:grid-cols-12">
                            <h2 class="w-full sm:col-span-12 py-2 border-b-2 border-charcoal-900 text-left font-medium text-charcoal-900">BPay</h2>
                            <input-text class="sm:col-span-4"
                                label="Biller Code"
                                placeholder="Biller Code"
                                name="bpay_biller_code"
                                v-model="item.bpay_biller_code"
                                :required="false" />
                            <input-text class="sm:col-span-4"
                                label="Customer Reference Number"
                                name="bpay_crn"
                                placeholder="Customer Reference Number"
                                v-model="item.bpay_crn"
                                :required="false" />
                        </div>
                        <div class="mt-2">
                            <h2 class="w-full sm:col-span-12 py-2 border-b-2 border-charcoal-900 text-left font-medium text-charcoal-900 mb-3">Documents/Files</h2>
                            <span v-for="(file, index) in item.documents" class="text-base mb-1 block flex flex-row items-center my-2">
                                <a :href="S3URL + '/' + file.path " target="_blank" class="text-gray-800 hover:text-default-500 truncate">{{ file.filename }}</a>
                                <button type="button" @click="removeUploadFile(index, 'documents')">
                                    <svg aria-hidden="true" focusable="false" data-prefix="far" data-icon="times" class="svg-inline--fa fa-times w-6 h-6 text-red-700 ml-2" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
                                        <path fill="currentColor" d="M207.6 256l107.72-107.72c6.23-6.23 6.23-16.34 0-22.58l-25.03-25.03c-6.23-6.23-16.34-6.23-22.58 0L160 208.4 52.28 100.68c-6.23-6.23-16.34-6.23-22.58 0L4.68 125.7c-6.23 6.23-6.23 16.34 0 22.58L112.4 256 4.68 363.72c-6.23 6.23-6.23 16.34 0 22.58l25.03 25.03c6.23 6.23 16.34 6.23 22.58 0L160 303.6l107.72 107.72c6.23 6.23 16.34 6.23 22.58 0l25.03-25.03c6.23-6.23 6.23-16.34 0-22.58L207.6 256z"></path>
                                    </svg>
                                </button>
                            </span>
                            <FileUpload @uploaded="fileUploaded($event, 'documents')"/>
                        </div>
                    </ValidationObserver>
                </div>
                <div class="md:w-3/4 sm:w-full px-4 bg-gray-200 border-r border-gray-400 pb-12" v-show="activeTab === 'types'">
                    <h2 class="px-1 pt-4 border-b-2 border-black pb-1 font-semibold">Supplier Types</h2>
                    <p class="bg-blue-100 border-t border-b border-blue-500 text-blue-700 px-4 py-3 text-xs mt-4">
                        Select the Types that are applicable to this Supplier.
                    </p>
                    <div v-if="!supplierTypesLoading">
                        <div class="flex flex-row">
                            <input type="text" v-model="searchSupplierTypes" value="" placeholder="Filter Supplier Types by Name..." class="form-input w-full my-3 text-sm flex-grow mr-3" />
                        </div>
                        <table class="min-w-full text-xs">
                            <thead>
                                <tr>
                                    <th class="table-head-cell w-12"><input type="checkbox" value="all" name="selectAll" v-model="selectAllSupplierTypes"></th>
                                    <th class="table-head-cell pl-0">Name</th>
                                </tr>
                            </thead>
                            <tbody v-if="supplierTypes">
                                <tr v-for="(item, index) in supplierTypes" v-bind:key="index">
                                    <td class="table-body-cell w-12 py-2" :class="[index % 2 !== 0 ? 'even' : 'odd' ]"><input type="checkbox" :value="item.id" v-model="selectedSupplierTypes"></td>
                                    <td class="table-body-cell pl-0" :class="[ index % 2 !== 0 ? 'even' : 'odd' ]">{{ item.name }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <div class="md:w-1/4 sm:w-full px-4 sm:px-6 lg:px-6">
                    <ValidationObserver vid="details" :slim="true">
                        <div class="mt-6 grid grid-cols-1 row-gap-5 col-gap-4 sm:grid-cols-12">
                            <input-select class="sm:col-span-12"
                                label="Account Type"
                                placeholder="Account Type"
                                name="account_type_id"
                                v-model="item.account_type_id"
                                :initialValue="initialValue.supplier_account_type"
                                return="value"
                                :required="true"
                                rules="required"
                                :options="options.supplierAccountTypes"/>
                            <input-toggle class="sm:col-span-12"
                                label="Active"
                                name="active"
                                v-model="item.active"
                                :required="false" />
                        </div>
                    </ValidationObserver>
                </div>
            </div>
        </form>
    </ValidationObserver>
</template>

<script>
    import FormSetup from "../../mixins/Tabs";
    import FormButtons from "../../mixins/FormButtons";
    import axios from "axios";
    import { matchingStateOption, stateSelectOption } from "../../helpers/australianStates";

    export default {
        name: "SupplierForm",
        props: {
            id: {required: false},
            type: {required: true},
            return: {required: false, default: '/configuration/suppliers'},
            page: {required: false, default: 'suppliers'}
        },
        mixins: [
            FormSetup,
            FormButtons
        ],
        data: function () {
            return {
                resource: 'suppliers',
                prefix: 'configuration',
                pageActions: {
                    buttons: [
                        {
                            label: 'Save',
                            icon: 'save',
                            iconClass: 'text-green-600',
                            type: 'method',
                            method: 'save',
                        },
                        {
                            label: 'View',
                            icon: 'eye',
                            iconClass: 'text-blue-600',
                            type: 'method',
                            method: 'view',
                            display: this.type === "Edit"
                        },
                        {
                            label: 'Cancel',
                            icon: 'times',
                            iconClass: 'text-red-600',
                            type: 'method',
                            method: 'cancel',
                        }
                    ]
                },
                tabs: [
                    {
                        name: 'Details',
                        slug: 'details'
                    },
                    {
                        name: 'Types',
                        slug: 'types'
                    }
                ],
                startTab: null,
                loading: true,
                submitted: false,
                errorMessage: '',
                item: {},
                S3URL: process.env.VUE_APP_S3_URL,
                options: {
                    supplierTypes: [],
                    states: []
                },
                searchSupplierTypes: '',
                originalSupplierTypes: [],
                selectedSupplierTypes: [],
                supplierTypesLoading: true,
            }
        },
        methods: {
            getData: function () {
                this.$Progress.start();

                axios.get(process.env.VUE_APP_API_URL + '/suppliers/' + this.id, )
                    .then(response => {
                        this.item = response.data;

                        if (this.item.types){
                            this.selectedSupplierTypes = this.item.types.map(type => type.id);
                        }

                        this.$Progress.finish();
                        this.loading = false;
                    })
                    .catch(() => {
                        this.$Progress.finish();
                        this.loading = false;
                    });
            },
            submitForm: function () {
                this.submitted = false;

                this.$refs.form.validate().then(success => {
                    let _method;
                    let _url;

                    if (!success) {
                        this.submitted = true;
                        return;
                    }

                    this.submitted = true;

                    this.$store.commit('TOGGLE_BACKDROP', {open: true});
                    this.errorMessage = '';

                    this.item.types = this.selectedSupplierTypes;

                    if (this.id) {
                        _url = process.env.VUE_APP_API_URL + '/suppliers/' + this.id;
                        _method = 'PUT';
                    } else if (this.item.id) {
                        _url = process.env.VUE_APP_API_URL + '/suppliers/' + this.item.id;
                        _method = 'PUT';
                    } else {
                        _url = process.env.VUE_APP_API_URL + '/suppliers';
                        _method = 'POST';
                    }

                    axios({
                        method: _method,
                        url: _url,
                        data: this.item
                    }).then(response => {
                        if (response.data.success) {
                            this.$store.commit('TOGGLE_BACKDROP', {open: false});

                            if (this.saveClose) {
                                if (this.return) {
                                    this.$router.push(this.return);
                                } else {
                                    this.$router.push('/configuration/suppliers');
                                }
                            } else {
                                this.$router.push('/configuration/suppliers/'+response.data.data.id+'/view');
                                //this.item.id = response.data.data.id;
                            }

                            this.$notify({
                                group: 'main',
                                type: 'success',
                                title: 'Success',
                                text: response.data.message,
                                duration: 5000
                            });

                        } else {
                            if (response.data.message) {
                                this.errorMessage = response.data.message;
                            }
                            this.$store.commit('TOGGLE_BACKDROP', {open: false});
                        }
                    }).catch(error => {
                        if (error.response.data.message) {
                            this.errorMessage = error.response.data.message;
                        } else {
                            this.errorMessage = 'An error occurred. Please contact the System Administrator.';
                        }

                        this.$store.commit('TOGGLE_BACKDROP', {open: false});
                    });
                });
            },
            methodHandler: function (method) {
                this[method]();
            },
            actionHandler: function (data) {
                this[data.method](data);
            },
            setAddress: function(address){
                this.item.address_1 = address.address_1;
                this.item.address_2 = address.address_2;
                this.item.suburb = address.suburb;
                this.item.state = address.state;
                this.item.postcode = address.postcode;
                this.item.country = address.country;
            },
            fileUploaded(payload, key) {
                if (!this.item[key]){
                    this.item[key] = [];
                }

                payload.data.forEach(file => {
                    this.item[key].push(file)
                })
            },
            removeUploadFile(index, key){
                this.item[key].splice(index, 1)
            },
        },
        beforeMount() {
            this.$store.dispatch('GET_SUPPLIER_TYPES').then(() => {
                this.originalSupplierTypes = this.$store.getters.supplierTypes.map(function (type) {
                    return { id: type.id, name: type.name };
                });

                this.supplierTypesLoading = false;
            });

            // Account Types
            this.$store.dispatch('GET_SUPPLIER_ACCOUNT_TYPES').then(() => {
                this.options.supplierAccountTypes = this.$store.getters.supplierAccountTypes.map(function (type) {
                    return { label: type.name, value: type.id };
                });
            });

            // States
            this.$store.dispatch('GET_STATES').then(() => {
                this.options.states = this.$store.getters.states.map(stateSelectOption);
            });
        },
        mounted() {
            if (this.type === "New") {
                this.loading = false;
                this.$Progress.finish();
                // Se defaults here
            } else if (this.type === "Edit") {
                this.getData();
            }

            let windowLocationHash = window.location.hash.replace('#', '');

            if (windowLocationHash) {
                this.startTab = windowLocationHash;
            }
        },
        computed: {
            supplierTypes () {
                if (this.searchSupplierTypes) {
                    return this.originalSupplierTypes.filter(item => item.name.toUpperCase().includes(this.searchSupplierTypes.toUpperCase()));
                }
                return this.originalSupplierTypes;
            },
            selectAllSupplierTypes: {
                get: function () {
                    return this.supplierTypes ? this.selectedSupplierTypes.length === this.supplierTypes.length : false;
                },
                set: function (value) {
                    let selected = [];

                    if (value) {
                        this.supplierTypes.forEach(function (type) {
                            selected.push(type.id);
                        });
                    }

                    this.selectedSupplierTypes = selected;
                }
            },
            initialValue: function(){
                let output = {};

                if (this.item && this.item.type){
                    output['supplier_type'] = this.item.type.id;
                } else {
                    output['supplier_type'] = null;
                }

                if (this.item && this.item.accountType){
                    output['supplier_account_type'] = this.item.accountType.id;
                } else {
                    output['supplier_account_type'] = null;
                }

                if (this.item && this.item.state){
                    output['state'] = matchingStateOption(this.options.states, this.item.state)?.value ?? null;
                } else {
                    output['state'] =  null
                }

                if (this.item && this.item.postal_state){
                    output['postal_state'] = matchingStateOption(this.options.states, this.item.postal_state)?.value ?? null;
                } else {
                    output['postal_state'] =  null
                }

                return output;
            }
        }
    }
</script>

<style scoped>

</style>
